if (!customElements.get('custom-quick-add-opener')) {
class CustomQuickAddOpener extends HTMLElement {
  constructor() {
    super();
    this.onOpenerClick = this.onOpenerClick.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('button');
    this.productId = this.dataset.productId;
    if (this.button && !this.listenerBound) {
      this.button.addEventListener('click', this.onOpenerClick);
      this.listenerBound = true;
    }
  }

  getOverlay() {
    return document.getElementById(`CustomQuickAdd-${this.dataset.productId}`);
  }

  onOpenerClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const overlay = this.getOverlay();
    if (!overlay) return;

    document.querySelectorAll('custom-quick-add-overlay:not(.hidden)').forEach((openOverlay) => {
      if (openOverlay !== overlay) {
        openOverlay.classList.add('hidden');
      }
    });

    if (isMobileQuickAddViewport()) {
      if (overlay.parentNode !== document.body) {
        overlay.originalParent = overlay.parentNode;
        overlay.originalNextSibling = overlay.nextSibling;
        document.body.appendChild(overlay);
      }

      document.body.classList.add('card_product_pop_up', 'o-hidden');
    } else if (overlay.parentNode === document.body && overlay.originalParent) {
      overlay.originalParent.insertBefore(overlay, overlay.originalNextSibling);
      clearBodyScrollLockIfNeeded();
    }

    overlay.classList.remove('hidden');

    if (shouldSyncCollectionNotify(overlay)) {
      scheduleCollectionNotifyInit(overlay);
    }
  }
}

function resetWishlistQuickAddOverlayState(overlay) {
  if (!overlay?.classList.contains('custom-quick-add-overlay--wishlist')) {
    return;
  }

  if (isDesktopQuickAddViewport() && overlay.parentNode === document.body && overlay.originalParent) {
    overlay.originalParent.insertBefore(overlay, overlay.originalNextSibling);
  }

  const card =
    overlay.originalParent?.closest?.('.swym-wishlist-card') || overlay.closest('.swym-wishlist-card');

  card?.classList.remove('swym-wishlist-card--size-popup-open');
  card?.querySelector('.js-wishlist-add-to-cart')?.setAttribute('aria-expanded', 'false');
}

function hasOpenCollectionQuickAddOverlay() {
  return Array.from(document.querySelectorAll('custom-quick-add-overlay:not(.hidden)')).some(
    (overlay) => !overlay.classList.contains('custom-quick-add-overlay--wishlist')
  );
}

function clearBodyScrollLockIfNeeded() {
  if (!hasOpenCollectionQuickAddOverlay()) {
    document.body.classList.remove('card_product_pop_up', 'o-hidden');
  }
}

function isMobileQuickAddViewport() {
  return window.matchMedia('(max-width: 749px)').matches;
}

function isDesktopQuickAddViewport() {
  return window.matchMedia('(min-width: 750px)').matches;
}

function isCollectionProductGridPage() {
  return document.getElementById('product-grid')?.classList.contains('collection-page-product-grid');
}

function isCollectionQuickAddOverlayId(overlayId) {
  return (
    overlayId.startsWith('CustomQuickAdd-') &&
    !overlayId.startsWith('CustomQuickAdd-cart-drawer-') &&
    !overlayId.startsWith('CustomQuickAdd-cart-wishlist-')
  );
}

function isCollectionPageOverlay(overlay) {
  if (!overlay || overlay.classList.contains('custom-quick-add-overlay--wishlist')) {
    return false;
  }

  const overlayId = overlay.id || '';

  if (overlayId.startsWith('ProductStickyPopup-')) {
    return false;
  }

  if (!isCollectionQuickAddOverlayId(overlayId)) {
    return false;
  }

  return true;
}

function isCollectionCardWrapper(cardWrapper) {
  if (!cardWrapper) {
    return false;
  }

  return Boolean(
    cardWrapper.closest(
      '#product-grid.collection-page-product-grid, .featured_colletion_section, [id^="collection-"], .featured-collection, .collection, .product-grid'
    ) || cardWrapper.classList?.contains('card-wrapper')
  );
}

function getCollectionCardWrapper(el) {
  const cardWrapper = el?.closest?.('.card-wrapper');

  if (!cardWrapper || !isCollectionCardWrapper(cardWrapper)) {
    return null;
  }

  return cardWrapper;
}

function getCollectionCardOverlay(cardWrapper) {
  const inCard = cardWrapper?.querySelector('custom-quick-add-overlay:not(.custom-quick-add-overlay--wishlist)');

  if (inCard) {
    return inCard;
  }

  if (!cardWrapper) {
    return null;
  }

  return (
    Array.from(
      document.querySelectorAll('body > custom-quick-add-overlay:not(.custom-quick-add-overlay--wishlist)')
    ).find((overlay) => {
      const originalCard = overlay.originalParent?.closest?.('.card-wrapper');
      return originalCard === cardWrapper || overlay.originalParent === cardWrapper;
    }) || null
  );
}

function getCollectionCardVariantsData(cardWrapper) {
  const overlay = getCollectionCardOverlay(cardWrapper);

  if (!overlay) {
    return [];
  }

  if (Array.isArray(overlay.variantsData) && overlay.variantsData.length) {
    return overlay.variantsData;
  }

  try {
    const dataEl = overlay.querySelector('.cqa-variant-data');
    return dataEl ? JSON.parse(dataEl.textContent) : [];
  } catch (error) {
    return [];
  }
}

function findVariantInCollectionData(variantsData, variantId) {
  if (!variantsData?.length || variantId == null || variantId === '') {
    return null;
  }

  return variantsData.find((entry) => String(entry.id) === String(variantId)) || null;
}

function matchVariantFromOverlayInputs(overlay, variantsData) {
  if (!overlay || !variantsData?.length) {
    return null;
  }

  let selectedSize = '';
  let selectedColor = '';

  const checkedSize = overlay.querySelector('input[name^="cqa-size-"]:checked');
  if (checkedSize) {
    selectedSize = checkedSize.value;
  }

  const checkedColor = overlay.querySelector('input[name^="cqa-color-"]:checked');
  if (checkedColor) {
    selectedColor = checkedColor.value;
  }

  return (
    variantsData.find((variant) => {
      const hasSize = !selectedSize || variant.options.includes(selectedSize);
      const hasColor = !selectedColor || variant.options.includes(selectedColor);
      return hasSize && hasColor;
    }) || null
  );
}

function buildVariantFromId(cardWrapper, variantId) {
  if (!variantId) {
    return null;
  }

  const variantsData = getCollectionCardVariantsData(cardWrapper);
  const fromData = findVariantInCollectionData(variantsData, variantId);

  if (fromData) {
    return fromData;
  }

  const wishlistButton = cardWrapper?.querySelector('.swym-add-to-wishlist-view-product');
  const priceAttr = parseFloat(wishlistButton?.getAttribute('data-product-price') || '');
  const imageAttr = wishlistButton?.getAttribute('data-product-image') || '';

  return {
    id: variantId,
    price: Number.isFinite(priceAttr) ? Math.round(priceAttr * 100) : null,
    featured_image: imageAttr ? { src: imageAttr } : null,
  };
}

function resolveCollectionSelectedVariant(cardWrapper) {
  if (!cardWrapper) {
    return null;
  }

  const variantsData = getCollectionCardVariantsData(cardWrapper);
  const overlay = getCollectionCardOverlay(cardWrapper);
  const variantInput = overlay?.querySelector('.cqa-variant-id');

  if (variantInput?.value) {
    const fromInput = buildVariantFromId(cardWrapper, variantInput.value);
    if (fromInput) {
      return fromInput;
    }
  }

  const fromInputs = matchVariantFromOverlayInputs(overlay, variantsData);
  if (fromInputs) {
    return fromInputs;
  }

  if (cardWrapper.dataset.cardVariantId) {
    const fromCard = buildVariantFromId(cardWrapper, cardWrapper.dataset.cardVariantId);
    if (fromCard) {
      return fromCard;
    }
  }

  const gridItem = cardWrapper.closest('li[data-variant-id]');
  if (gridItem?.dataset.variantId) {
    const fromGridItem = buildVariantFromId(cardWrapper, gridItem.dataset.variantId);
    if (fromGridItem) {
      return fromGridItem;
    }
  }

  const wishlistButton = cardWrapper.querySelector('.swym-add-to-wishlist-view-product');
  if (wishlistButton?.getAttribute('data-variant-id')) {
    return buildVariantFromId(cardWrapper, wishlistButton.getAttribute('data-variant-id'));
  }

  return null;
}

function getCollectionVariantImageUrl(variant, cardWrapper) {
  if (variant?.featured_image?.src) {
    return variant.featured_image.src;
  }

  const mainImage = cardWrapper?.querySelector('.card__media img');
  return mainImage?.getAttribute('src') || '';
}

function buildCollectionVariantProductUrl(button, variantId) {
  const productUrl = button?.getAttribute('data-product-url') || '';

  if (!productUrl || !variantId) {
    return productUrl;
  }

  try {
    const isAbsolute = productUrl.startsWith('http');
    const parsed = new URL(productUrl, window.location.origin);

    parsed.searchParams.set('variant', String(variantId));

    if (isAbsolute) {
      return parsed.toString();
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (error) {
    const cleanUrl = productUrl.split('?')[0].split('#')[0];
    return `${cleanUrl}?variant=${variantId}`;
  }
}

function syncCollectionWishlistButton(cardWrapper, variant) {
  if (!isCollectionCardWrapper(cardWrapper) || !variant?.id) {
    return;
  }

  const wishlistButton = cardWrapper.querySelector('.swym-add-to-wishlist-view-product');
  if (!wishlistButton) {
    return;
  }

  const variantId = String(variant.id);

  wishlistButton.setAttribute('data-variant-id', variantId);
  wishlistButton.setAttribute('data-product-url', buildCollectionVariantProductUrl(wishlistButton, variantId));

  const imageUrl = getCollectionVariantImageUrl(variant, cardWrapper);
  if (imageUrl) {
    wishlistButton.setAttribute('data-product-image', imageUrl);
  }

  if (variant.price != null) {
    wishlistButton.setAttribute('data-product-price', String(variant.price / 100));
  }
}

function getCollectionPriceConfig() {
  return window.eseoeseCollectionPrice || {};
}

function getCollectionCardPriceElement(cardWrapper) {
  return cardWrapper?.querySelector('.card_product_info .card-information > .price') || null;
}

function formatCollectionMoney(cents, currencySymbol) {
  const symbol =
    currencySymbol ||
    getCollectionPriceConfig().currencySymbol ||
    window.Shopify?.currency?.symbol ||
    '€';
  const amount = (cents / 100).toFixed(2).replace('.', ',');
  return `${amount}${symbol}`;
}

function syncCollectionCardPrice(cardWrapper, variant) {
  if (!isCollectionCardWrapper(cardWrapper) || variant?.price == null) {
    return;
  }

  const priceElement = getCollectionCardPriceElement(cardWrapper);
  if (!priceElement) {
    return;
  }

  const config = getCollectionPriceConfig();
  const currencySymbol = config.currencySymbol || '';
  const price = variant.price;
  const compareAtPrice = variant.compare_at_price;
  const onSale = compareAtPrice != null && compareAtPrice > price;
  const formattedPrice = formatCollectionMoney(price, currencySymbol);

  const regularPriceItem = priceElement.querySelector('.price__regular .price-item--regular');
  const salePriceItem = priceElement.querySelector('.price__sale .price-item--sale');
  const compareAtItem = priceElement.querySelector('.price__sale s.price-item--regular');
  const discountItem = priceElement.querySelector('.price-item--discount');

  if (regularPriceItem) {
    regularPriceItem.textContent = formattedPrice;
  }

  if (salePriceItem) {
    salePriceItem.textContent = formattedPrice;
  }

  if (onSale) {
    priceElement.classList.add('price--on-sale');

    if (compareAtItem) {
      compareAtItem.textContent = formatCollectionMoney(compareAtPrice, currencySymbol);
    }

    if (discountItem && config.showDiscount) {
      const discountPercent = Math.round(((compareAtPrice - price) * 100) / compareAtPrice);
      discountItem.textContent = `${discountPercent}%`;
    }
  } else {
    priceElement.classList.remove('price--on-sale');
  }

  priceElement.classList.toggle('price--sold-out', variant.available === false);
}

function refreshCollectionWishlistButtonFromCard(cardWrapper) {
  const variant = resolveCollectionSelectedVariant(cardWrapper);

  if (variant) {
    syncCollectionWishlistButton(cardWrapper, variant);
  }
}

function buildCollectionWishlistProductData(button, cardWrapper, variant) {
  const productId = button.getAttribute('data-product-id');
  const variantId = String(variant.id);
  const productUrl = buildCollectionVariantProductUrl(button, variantId);
  const productImage = getCollectionVariantImageUrl(variant, cardWrapper) || button.getAttribute('data-product-image') || '';
  const productTitle = button.getAttribute('data-product-title') || '';
  const productPrice =
    variant.price != null
      ? variant.price / 100
      : parseFloat(button.getAttribute('data-product-price') || 0);

  return {
    empi: productId ? parseInt(productId, 10) : undefined,
    epi: parseInt(variantId, 10),
    du: productUrl || window.location.href,
    iu: productImage,
    dt: productTitle,
    pr: Number.isFinite(productPrice) ? productPrice : 0,
    et: 4,
  };
}

function saveCollectionVariantToWishlist(button, cardWrapper, variant) {
  refreshCollectionWishlistButtonFromCard(cardWrapper);

  const productData = buildCollectionWishlistProductData(button, cardWrapper, variant);

  const onSuccess = function () {
    button.classList.add('swym-added');
    delete button.dataset.processingWishlist;

    const wishlistElement = document.querySelector('swym-wishlist');
    if (wishlistElement && typeof wishlistElement.invalidateWishlistRender === 'function') {
      wishlistElement.invalidateWishlistRender();
    }

    if (window._swat) {
      const upi = window._swat.persistedData?.upi;
      const count = Array.isArray(upi) ? upi.length : 0;
      document.querySelectorAll('.js-wishlist-count').forEach((el) => {
        el.textContent = count;
      });
    }
  };

  const onError = function () {
    delete button.dataset.processingWishlist;
  };

  const saveToSwym = function (swat) {
    if (!swat || typeof swat.addToWishList !== 'function') {
      onError();
      return;
    }

    swat.addToWishList(productData, onSuccess, onError);
  };

  if (window._swat) {
    saveToSwym(window._swat);
  } else if (window.SwymCallbacks && typeof window.SwymCallbacks.push === 'function') {
    window.SwymCallbacks.push(saveToSwym);
  } else {
    onError();
  }
}

function handleCollectionWishlistClick(event) {
  const wishlistButton = event.target.closest(
    '.product-grid .swym-add-to-wishlist-view-product, .featured_colletion_section .swym-add-to-wishlist-view-product, #product-grid.collection-page-product-grid .swym-add-to-wishlist-view-product'
  );

  if (!wishlistButton) {
    return;
  }

  if (wishlistButton.classList.contains('swym-added')) {
    return;
  }

  const cardWrapper = getCollectionCardWrapper(wishlistButton);
  if (!cardWrapper) {
    return;
  }

  const variant = resolveCollectionSelectedVariant(cardWrapper);
  if (!variant?.id) {
    return;
  }

  if (wishlistButton.dataset.processingWishlist === 'true') {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();

  wishlistButton.dataset.processingWishlist = 'true';
  saveCollectionVariantToWishlist(wishlistButton, cardWrapper, variant);
}

function restoreQuickAddOverlayToCard(overlay) {
  if (!overlay || overlay.parentNode !== document.body || !overlay.originalParent) {
    return;
  }

  overlay.originalParent.insertBefore(overlay, overlay.originalNextSibling);
}

function restoreCollectionOverlaysFromBodyOnDesktop() {
  if (!isDesktopQuickAddViewport()) {
    return;
  }

  document
    .querySelectorAll('body > custom-quick-add-overlay:not(.custom-quick-add-overlay--wishlist)')
    .forEach((overlay) => {
      restoreQuickAddOverlayToCard(overlay);
      overlay.classList.add('hidden');
    });

  clearBodyScrollLockIfNeeded();
}

function isPdpStickyPopupOverlay(overlay) {
  const overlayId = overlay?.id || '';
  return overlayId.startsWith('ProductStickyPopup-');
}

function shouldSyncCollectionNotify(overlay) {
  return isCollectionPageOverlay(overlay) || isPdpStickyPopupOverlay(overlay);
}

function removeCollectionNotifyNode(node) {
  if (!node) {
    return;
  }

  const portal = node.closest('.nm-portal');

  if (portal && portal !== node) {
    portal.remove();
    return;
  }

  node.remove();
}

function dedupeCollectionNotifyInSlot(overlay) {
  if (!shouldSyncCollectionNotify(overlay)) {
    return;
  }

  const notifySlot = overlay.querySelector('.cqa-notify-slot');

  if (!notifySlot) {
    return;
  }

  const notifyButtons = Array.from(overlay.querySelectorAll('.restock-alerts-notify-button'));

  notifyButtons.slice(1).forEach((button) => {
    removeCollectionNotifyNode(button);
  });

  const placeholderAnchor = notifySlot.querySelector('.cqa-notify-anchor:not(.restock-alerts-notify-button)');
  const primaryNotify = notifySlot.querySelector('.restock-alerts-notify-button');

  if (primaryNotify && placeholderAnchor && primaryNotify !== placeholderAnchor) {
    placeholderAnchor.remove();
  }

  if (primaryNotify) {
    const primaryRoot = primaryNotify.closest('.nm-portal') || primaryNotify;

    if (!notifySlot.contains(primaryRoot)) {
      notifySlot.appendChild(primaryRoot);
    }
  }

  normalizeCollectionNotifyLabel(notifySlot);
}

function applyComingSoonButtonLabel(button, label, iconUrl) {
  if (!button || !label) {
    return;
  }

  const slot = button.closest('.cqa-notify-slot');
  const resolvedIconUrl =
    iconUrl || slot?.dataset?.notifyIcon || window.variantStrings?.comingSoonIconUrl || '';
  let labelEl = button.querySelector('.cqa-notify-label');
  let iconEl = button.querySelector('.cqa-coming-soon-icon');

  if (!labelEl) {
    const existingIcon = iconEl;
    button.textContent = '';
    if (existingIcon) {
      button.appendChild(existingIcon);
      iconEl = existingIcon;
    } else if (resolvedIconUrl) {
      iconEl = document.createElement('img');
      iconEl.className = 'cqa-coming-soon-icon';
      iconEl.src = resolvedIconUrl;
      iconEl.alt = '';
      iconEl.loading = 'lazy';
      iconEl.width = 18;
      iconEl.height = 18;
      button.appendChild(iconEl);
    }
    labelEl = document.createElement('span');
    labelEl.className = 'cqa-notify-label';
    button.appendChild(labelEl);
  } else if (!iconEl && resolvedIconUrl) {
    iconEl = document.createElement('img');
    iconEl.className = 'cqa-coming-soon-icon';
    iconEl.src = resolvedIconUrl;
    iconEl.alt = '';
    iconEl.loading = 'lazy';
    iconEl.width = 18;
    iconEl.height = 18;
    button.insertBefore(iconEl, labelEl);
  }

  if (labelEl.textContent !== label) {
    labelEl.textContent = label;
  }
}

window.applyComingSoonButtonLabel = applyComingSoonButtonLabel;

function normalizeCollectionNotifyLabel(notifySlot) {
  if (!notifySlot) {
    return;
  }

  const label = (notifySlot.dataset.notifyLabel || window.variantStrings?.notifyMe || 'COMING SOON').toUpperCase();
  const buttons = notifySlot.querySelectorAll(
    '.cqa-notify-anchor, .restock-alerts-notify-button'
  );

  buttons.forEach((button) => {
    applyComingSoonButtonLabel(button, label, notifySlot.dataset.notifyIcon);
  });
}


function syncCollectionNotifyVariantInput(variantId) {
  window._ReStockConfig = window._ReStockConfig || window._RestockConfig || {};
  window._RestockConfig = window._ReStockConfig;
  window._ReStockConfig['developer_mode_variant_listener'] = true;

  let restockInput = document.getElementById('restock-variant-change');

  if (!restockInput && variantId != null && variantId !== '') {
    restockInput = document.createElement('input');
    restockInput.type = 'hidden';
    restockInput.id = 'restock-variant-change';
    document.body.appendChild(restockInput);
  }

  if (restockInput && variantId != null && variantId !== '') {
    restockInput.value = String(variantId);
    restockInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function selectDefaultCollectionSize(overlay) {
  if (!isCollectionPageOverlay(overlay)) {
    return;
  }

  const sizeInputs = overlay.querySelectorAll('input[name^="cqa-size-"]');

  if (!sizeInputs.length) {
    return;
  }

  const hasChecked = Array.from(sizeInputs).some((input) => input.checked);

  if (hasChecked) {
    return;
  }

  sizeInputs[0].checked = true;
}

function syncCollectionNotifySlot(overlay, variant) {
  if (!shouldSyncCollectionNotify(overlay)) {
    return;
  }

  const notifySlot = overlay.querySelector('.cqa-notify-slot');
  const formWrapper = overlay.querySelector('.cqa-form-wrapper');
  const addToCartForm = overlay.querySelector('.cqa-form-wrapper form[data-type="add-to-cart-form"]');
  const submitButton = overlay.querySelector('.cqa-add-btn');
  const anchor = overlay.querySelector('.cqa-notify-anchor');

  if (!notifySlot) {
    return;
  }

  const showNotify = Boolean(variant && variant.available === false);

  if (showNotify) {
    formWrapper?.classList.add('cqa-form-wrapper--show-notify');
    notifySlot.hidden = false;
    notifySlot.removeAttribute('hidden');
    notifySlot.style.removeProperty('display');

    if (addToCartForm) {
      addToCartForm.hidden = true;
    }

    if (submitButton) {
      submitButton.hidden = true;
    }

    if (anchor && variant.id) {
      anchor.setAttribute('data-variant-id', String(variant.id));
      anchor.hidden = false;
      anchor.removeAttribute('hidden');
      anchor.style.removeProperty('display');
    }

    const restockBtn = notifySlot.querySelector('.restock-alerts-notify-button');
    if (restockBtn && variant.id) {
      restockBtn.setAttribute('data-variant-id', String(variant.id));
      restockBtn.hidden = false;
      restockBtn.removeAttribute('hidden');
      restockBtn.style.removeProperty('display');
    }

    syncCollectionNotifyVariantInput(variant.id);
    dedupeCollectionNotifyInSlot(overlay);
    normalizeCollectionNotifyLabel(notifySlot);
  } else {
    formWrapper?.classList.remove('cqa-form-wrapper--show-notify');
    notifySlot.hidden = true;
    notifySlot.setAttribute('hidden', '');

    if (addToCartForm) {
      addToCartForm.hidden = false;
    }

    if (submitButton) {
      submitButton.hidden = false;
    }
  }
}

function scheduleCollectionNotifyInit(overlay) {
  if (!shouldSyncCollectionNotify(overlay)) {
    return;
  }

  const runPass = () => {
    if (overlay.classList.contains('hidden')) {
      return;
    }

    selectDefaultCollectionSize(overlay);

    if (typeof overlay.updateVariant === 'function') {
      overlay.updateVariant();
    }

    dedupeCollectionNotifyInSlot(overlay);
  };

  runPass();
  [200, 500, 1000].forEach((delay) => setTimeout(runPass, delay));
}

function isWishlistOverlayCloseSuppressed(overlay) {
  if (!overlay?.classList.contains('custom-quick-add-overlay--wishlist')) {
    return false;
  }

  const openedAt = Number(overlay.dataset.openedAt || 0);

  return openedAt > 0 && Date.now() - openedAt < 500;
}

function hideQuickAddOverlay(overlay) {
  if (isWishlistOverlayCloseSuppressed(overlay)) {
    return;
  }

  overlay.classList.add('hidden');

  if (isCollectionPageOverlay(overlay)) {
    restoreQuickAddOverlayToCard(overlay);
  }

  resetWishlistQuickAddOverlayState(overlay);
  clearBodyScrollLockIfNeeded();
}

// Close overlays when clicking anywhere outside
document.addEventListener('click', (e) => {
  if (e.target.closest('.custom-quick-add-overlay--wishlist')) {
    if (e.target.closest('.cqa-close') || e.target.closest('.cqa-backdrop')) {
      const overlay = e.target.closest('custom-quick-add-overlay');
      if (overlay) {
        hideQuickAddOverlay(overlay);
      }
    }
    return;
  }

  if (e.target.closest('.cqa-close')) {
    const overlay = e.target.closest('custom-quick-add-overlay');
    if (overlay) {
      hideQuickAddOverlay(overlay);
    }
    return;
  }

  if (e.target.closest('.cqa-content')) return;
  if (e.target.closest('.sticky_add_to_cart_btn')) return;
  if (e.target.closest('custom-quick-add-opener')) return;
  if (e.target.closest('.js-wishlist-add-to-cart[aria-expanded="true"]')) return;
  if (e.target.closest('.js-wishlist-add-to-cart[data-processing-add="true"]')) return;

  document.querySelectorAll('custom-quick-add-overlay:not(.hidden)').forEach((overlay) => {
    hideQuickAddOverlay(overlay);
  });
});

document.addEventListener('click', (e) => {
  const anchor = e.target.closest('.cqa-notify-anchor:not(.restock-alerts-notify-button)');
  if (!anchor) return;

  const overlay = anchor.closest('custom-quick-add-overlay');
  const restockButton =
    overlay?.querySelector('.restock-alerts-notify-button') ||
    document.querySelector(`.restock-alerts-notify-button[id="${anchor.id}"]`);

  if (restockButton && restockButton !== anchor) {
    e.preventDefault();
    restockButton.click();
  }
});
customElements.define('custom-quick-add-opener', CustomQuickAddOpener);

class CustomQuickAddOverlay extends HTMLElement {
  constructor() {
    super();
    this.onVariantInputChange = this.onVariantInputChange.bind(this);
    this.onColorInputChange = this.onColorInputChange.bind(this);
  }

  isWishlistOverlay() {
    return this.classList.contains('custom-quick-add-overlay--wishlist');
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    }

    if (this.isWishlistOverlay()) {
      this.initializeOverlay();
      return;
    }

    setTimeout(() => {
      this.initializeOverlay();
    }, 0);
  }

  initializeOverlay() {
    if (this.initialized) {
      return;
    }

    this.closeButton = this.querySelector('.cqa-close');
    this.sizeInputs = this.querySelectorAll('input[name^="cqa-size-"]');
    this.colorInputs = this.querySelectorAll('input[name^="cqa-color-"]');
    this.variantIdInput = this.querySelector('.cqa-variant-id');
    this.form = this.querySelector('form');
    this.colorNameDisplay = this.querySelector('.cqa-color-name');
    this.cardWrapper = this.closest('.card-wrapper');

    try {
      const dataEl = this.querySelector('.cqa-variant-data');
      this.variantsData = dataEl ? JSON.parse(dataEl.textContent) : [];
    } catch (e) {
      this.variantsData = [];
    }

    this.bindEvents();
    this.setupCollectionNotifyObserver();
    selectDefaultCollectionSize(this);
    this.updateVariant();
    this.initialized = true;
  }

  setupCollectionNotifyObserver() {
    if (!shouldSyncCollectionNotify(this) || this._notifyObserver) {
      return;
    }

    const notifySlot = this.querySelector('.cqa-notify-slot');

    if (!notifySlot) {
      return;
    }

    this._notifyObserver = new MutationObserver(() => {
      dedupeCollectionNotifyInSlot(this);
    });

    this._notifyObserver.observe(notifySlot, { childList: true, subtree: true });
  }

  onVariantInputChange() {
    this.updateVariant();
  }

  onColorInputChange(event) {
    if (this.colorNameDisplay) {
      this.colorNameDisplay.textContent = event.target.value.toUpperCase();
    }

    this.querySelectorAll('.cqa-color-swatches .swatch').forEach((swatch) => {
      swatch.classList.remove('selected');
    });
    event.target.closest('.swatch')?.classList.add('selected');

    this.updateVariant({ colorChanged: true });
  }

  bindEvents() {
    if (this._eventsBound) {
      return;
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        hideQuickAddOverlay(this);
      });
    }

    const backdrop = this.querySelector('.cqa-backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', () => {
        hideQuickAddOverlay(this);
      });
    }

    this.sizeInputs.forEach((input) => {
      input.addEventListener('change', this.onVariantInputChange);
      input.addEventListener('input', this.onVariantInputChange);
    });

    this.colorInputs.forEach((input) => {
      input.addEventListener('change', this.onColorInputChange);
      input.addEventListener('input', this.onColorInputChange);
    });

    this.querySelectorAll('.cqa-size-item').forEach((label) => {
      label.addEventListener('click', () => {
        const input = label.querySelector('input[type="radio"]');
        const allowDisabledSelection = shouldSyncCollectionNotify(this);

        if (!input) {
          return;
        }

        if (label.classList.contains('is-disabled') && !allowDisabledSelection) {
          return;
        }

        input.checked = true;
        this.updateVariant();
      });
    });

    if (this.form) {
      this.form.addEventListener('submit', () => {
        if (this.isWishlistOverlay()) {
          hideQuickAddOverlay(this);
        }
      });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (this.isWishlistOverlay()) {
          hideQuickAddOverlay(this);
        }
      });
    }

    this._eventsBound = true;
  }

  setSubmitButtonState(submitButton, submitButtonText, state) {
    if (!submitButton || !submitButtonText) {
      return;
    }

    const labels = {
      available: window.variantStrings?.addToCart || 'AÑADIR AL CARRITO',
      soldOut: window.variantStrings?.soldOut || 'AGOTADO',
      unavailable: window.variantStrings?.unavailable || 'NO DISPONIBLE',
    };

    if (state === 'available') {
      submitButtonText.textContent = labels.available;
      submitButton.classList.remove('disabled');
    } else if (state === 'soldOut') {
      submitButtonText.textContent = labels.soldOut;
      submitButton.classList.add('disabled');
    } else {
      submitButtonText.textContent = labels.unavailable;
      submitButton.classList.add('disabled');
    }

    if (this.isWishlistOverlay()) {
      submitButton.removeAttribute('disabled');
      submitButton.setAttribute('aria-disabled', state === 'available' ? 'false' : 'true');
      return;
    }

    submitButton.disabled = state !== 'available';
    submitButton.setAttribute('aria-disabled', state === 'available' ? 'false' : 'true');
  }

  updateVariant(options = {}) {
    let selectedSize = '';
    let selectedColor = '';

    const checkedSize = Array.from(this.sizeInputs).find(input => input.checked);
    if (checkedSize) selectedSize = checkedSize.value;

    const checkedColor = Array.from(this.colorInputs).find(input => input.checked);
    if (checkedColor) selectedColor = checkedColor.value;

    const submitButton = this.querySelector('[name="add"]');
    const submitButtonText = submitButton ? submitButton.querySelector('span') : null;

    if (isCollectionPageOverlay(this) && this.sizeInputs.length > 0 && !checkedSize) {
      if (this.variantIdInput) {
        this.variantIdInput.value = '';
      }

      if (submitButton && submitButtonText) {
        this.setSubmitButtonState(submitButton, submitButtonText, 'unavailable');
      }

      syncCollectionNotifySlot(this, null);
      return;
    }

    const matchingVariant = this.variantsData.find(variant => {
      const hasSize = !selectedSize || variant.options.includes(selectedSize);
      const hasColor = !selectedColor || variant.options.includes(selectedColor);
      return hasSize && hasColor;
    });

    // Update size labels availability and stock warning dynamically based on selected color
    this.sizeInputs.forEach(input => {
      const sizeValue = input.value;
      const variantForThisSize = this.variantsData.find(v => {
        const matchSize = v.options.includes(sizeValue);
        const matchColor = !selectedColor || v.options.includes(selectedColor);
        return matchSize && matchColor;
      });

      const label = input.closest('.cqa-size-item');
      if (label && variantForThisSize) {
        // Clear previous warnings/icons
        const existingWarning = label.querySelector('.cqa-stock-warning');
        if (existingWarning) existingWarning.remove();
        const existingIcon = label.querySelector('.cqa-envelope-icon');
        if (existingIcon) existingIcon.remove();

        if (variantForThisSize.available) {
          label.classList.remove('is-disabled');
          input.dataset.available = "true";
          
          const lowStockEnabled = this.hasAttribute('data-low-stock-enabled');
          const threshold = lowStockEnabled ? (parseInt(this.getAttribute('data-low-stock-threshold')) || 0) : -1;
          const warningText = this.getAttribute('data-low-stock-text') || '';

          if (lowStockEnabled && variantForThisSize.inventory_quantity > 0 && variantForThisSize.inventory_quantity <= threshold) {
            const warning = document.createElement('span');
            warning.className = 'cqa-stock-warning';
            warning.textContent = warningText;
            label.appendChild(warning);
          }
        } else {
          label.classList.add('is-disabled');
          input.removeAttribute('data-available');
          
          const icon = document.createElement('span');
          icon.className = 'cqa-envelope-icon';
          icon.innerHTML = `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M2.22075 13.0036C2.12362 12.8311 2.0625 12.6031 2.0625 12.3578V4.98007M15.9375 4.96582V12.3582C15.9375 12.5974 15.882 12.8172 15.7849 12.9897M6.48563 9.06832L7.72763 10.2177C7.79438 10.2787 7.861 10.3316 7.9275 10.3763C8.6025 10.8773 9.38887 10.8773 10.0612 10.3733C10.125 10.3291 10.1921 10.2732 10.2532 10.2177L11.5035 9.0627" stroke="#B4B4B4" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M15.7848 12.9897C15.6678 13.2042 15.4875 13.3434 15.2872 13.3434H2.7127C2.51808 13.3434 2.34033 13.2125 2.2207 13.0036" stroke="#B4B4B4" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M15.9375 4.96574L11.5031 9.06261L10.2529 10.2176C10.1917 10.2735 10.1254 10.329 10.0616 10.3736C9.38887 10.8772 8.60288 10.8772 7.92788 10.3762C7.85713 10.3286 7.79018 10.2756 7.72763 10.2176L6.48563 9.06824L2.0625 4.97961L2.1015 4.92411C2.21775 4.75686 2.37637 4.65674 2.54887 4.65674H15.4624C15.6263 4.65674 15.7849 4.75424 15.8985 4.91024C15.9127 4.92724 15.9257 4.94574 15.9375 4.96574Z" stroke="#B4B4B4" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
          label.appendChild(icon);
        }
      }
    });

    if (matchingVariant) {
      this.variantIdInput.value = matchingVariant.id;

      if (this.cardWrapper && isCollectionCardWrapper(this.cardWrapper)) {
        syncCollectionWishlistButton(this.cardWrapper, matchingVariant);
        syncCollectionCardPrice(this.cardWrapper, matchingVariant);
      }

      if (options.colorChanged && matchingVariant.featured_image && matchingVariant.featured_image.src) {
        if (this.cardWrapper) {
          const mainImage = this.cardWrapper.querySelector('.card__media img');
          if (mainImage) {
            const imgSrc = matchingVariant.featured_image.src;
            mainImage.src = imgSrc;
            if (typeof buildShopifySrcset === 'function') {
              mainImage.srcset = buildShopifySrcset(imgSrc);
            } else {
              const widths = [360, 533, 720, 940, 1066, 1445, 1680, 1946];
              mainImage.srcset = widths.map(w => (imgSrc.includes('?') ? `${imgSrc}&width=${w}` : `${imgSrc}?width=${w}`) + ` ${w}w`).join(', ');
            }
            
            // If the card has a slider, scroll back to the first slide to show the updated image
            const sliderComponent = this.cardWrapper.querySelector('card-product-slider');
            if (sliderComponent && sliderComponent.slider) {
              sliderComponent.slider.scrollTo({ left: 0, behavior: 'smooth' });
            }
            
            const flickityComponent = this.cardWrapper.querySelector('flickity-product-slider');
            if (flickityComponent && flickityComponent.flickity) {
              flickityComponent.flickity.select(0);
            }
          }
        }
      }

      // Update Main Product Page if this is the sticky popup
      // Do not run while the overlay is closed — init would reset the PDP to the first color.
      if (this.id && this.id.includes('ProductStickyPopup-') && !this.classList.contains('hidden')) {
        const sectionId = this.id.replace('ProductStickyPopup-', '');
        const nativeVariantSelects = document.querySelector(`variant-selects[data-section="${sectionId}"]`);
        if (nativeVariantSelects) {
          let changed = false;
          let inputToTrigger = null;
          
          if (selectedSize) {
            const nativeSizeInput = Array.from(nativeVariantSelects.querySelectorAll('input[type="radio"]')).find(
              (input) => input.value === selectedSize
            );
            if (nativeSizeInput && !nativeSizeInput.checked) {
              nativeSizeInput.checked = true;
              changed = true;
              inputToTrigger = nativeSizeInput;
            }
          }
          if (selectedColor) {
            const nativeColorInput = Array.from(nativeVariantSelects.querySelectorAll('input[type="radio"]')).find(
              (input) => input.value === selectedColor
            );
            if (nativeColorInput && !nativeColorInput.checked) {
              nativeColorInput.checked = true;
              changed = true;
              inputToTrigger = nativeColorInput;
            }
          }

          if (changed && inputToTrigger) {
            inputToTrigger.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        if (options.colorChanged && matchingVariant.featured_media_id) {
          const mediaGallery = document.querySelector(`#MediaGallery-${sectionId}`);
          if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
            mediaGallery.setActiveMedia(`${sectionId}-${matchingVariant.featured_media_id}`, true);
          }
        }
      }

      const useNotifyInsteadOfSoldOut =
        shouldSyncCollectionNotify(this) && matchingVariant.available === false;

      if (submitButton && submitButtonText && !useNotifyInsteadOfSoldOut) {
        this.setSubmitButtonState(
          submitButton,
          submitButtonText,
          matchingVariant.available ? 'available' : 'soldOut'
        );
      }

      syncCollectionNotifySlot(this, matchingVariant);
    } else if (submitButton && submitButtonText) {
      this.setSubmitButtonState(submitButton, submitButtonText, 'unavailable');
      syncCollectionNotifySlot(this, null);
    }
  }
}
customElements.define('custom-quick-add-overlay', CustomQuickAddOverlay);

function removeCartDrawerQuickAddOverlaysFromBody() {
  document
    .querySelectorAll(
      'body > custom-quick-add-overlay[id^="CustomQuickAdd-cart-drawer-"], body > custom-quick-add-overlay[id^="CustomQuickAdd-cart-wishlist-"]'
    )
    .forEach((el) => {
      el.remove();
    });
}

function hideAllOpenQuickAddOverlays() {
  document.querySelectorAll('custom-quick-add-overlay:not(.hidden)').forEach((el) => {
    hideQuickAddOverlay(el);
  });
}

function hideOpenCollectionQuickAddOverlays() {
  document.querySelectorAll('custom-quick-add-overlay:not(.hidden)').forEach((el) => {
    if (el.classList.contains('custom-quick-add-overlay--wishlist')) {
      return;
    }

    hideQuickAddOverlay(el);
  });
}

window.hideQuickAddOverlay = hideQuickAddOverlay;
window.hideAllOpenQuickAddOverlays = hideAllOpenQuickAddOverlays;

document.addEventListener('cart-drawer:updated', () => {
  removeCartDrawerQuickAddOverlaysFromBody();
});
document.addEventListener('cart-drawer:open', () => {
  removeCartDrawerQuickAddOverlaysFromBody();
  hideOpenCollectionQuickAddOverlays();
});
document.addEventListener('cart-drawer:close', () => {
  hideAllOpenQuickAddOverlays();
  if (typeof window.closeCartDrawerSizeSelectors === 'function') {
    window.closeCartDrawerSizeSelectors();
  }
});

document.addEventListener('click', handleCollectionWishlistClick, true);

restoreCollectionOverlaysFromBodyOnDesktop();
window.addEventListener('resize', restoreCollectionOverlaysFromBodyOnDesktop);
document.addEventListener('DOMContentLoaded', restoreCollectionOverlaysFromBodyOnDesktop);
}
